/**
 * Главные цифры на «Сегодня» — §2 ТЗ портала: «мои дела от всех агентов
 * одной лентой; главные цифры по правам».
 *
 * Цифры не считаются здесь и не приходят особым адресом «для главной»:
 * плитки собираются из тех же ответов, что показывают сами разделы —
 * обзор финансов, платёжки недели, долги, чек-листы дня. Второй источник
 * тех же чисел однажды разошёлся бы с первым, и разошёлся бы молча; так
 * «Сегодня» и раздел не могут показать разное.
 *
 * Что показано — по правам, и не «по нашему мнению», а потому что запрос
 * делается только туда, куда человеку можно: без права на оплаты запроса
 * о долгах нет вовсе, а не «есть, но ответ спрятан».
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { metricValue } from '@finance/components';
import type { Overview } from '@finance/api';
import { Link } from '../router';
import { rub } from '../api';
import type { Navigation } from '../api';
import { canSee } from '../nav';
import { financeApi } from '../sections/finance/api';
import { paymentsApi } from '../sections/payments/api';
import { paymentTabs } from '../sections/payments/paths';
import { shiftApi } from '../sections/shift/api';
import type { ChecklistDay } from '../sections/shift/api';
import type { Debts, Week } from '../sections/payments/api';

interface Tile {
  key: string;
  title: string;
  value: string;
  hint?: string | null;
  link: string;
  tone?: 'red' | null;
}

/** Плитки живут минуту: «Сегодня» открывают и держат открытым. */
const FRESH = { staleTime: 60_000, refetchOnWindowFocus: true } as const;

export function Numbers({ nav }: { nav: Navigation }) {
  const seesFinance = canSee('finance', nav);
  const seesPayments = canSee('payments', nav);
  // Плитки — по вкладкам «Оплат» (D235): неделя и долги — свои галочки.
  const payTabs = seesPayments ? paymentTabs(nav) : [];
  const seesShift = canSee('shift', nav);

  const overview = useQuery<Overview, ApiError>({
    queryKey: ['today', 'overview'],
    queryFn: () => financeApi.overview({}),
    // Плитки выручки — из «Обзора», у него своя галочка (D235).
    enabled: seesFinance && (nav.user.is_host || Boolean(nav.services.finance?.rights?.overview)),
    ...FRESH,
  });
  const week = useQuery<Week, ApiError>({
    queryKey: ['today', 'week'],
    queryFn: () => paymentsApi.week(),
    enabled: payTabs.includes('week'),
    ...FRESH,
  });
  const debts = useQuery<Debts, ApiError>({
    queryKey: ['today', 'debts'],
    queryFn: () => paymentsApi.debts(),
    enabled: payTabs.includes('debts'),
    ...FRESH,
  });
  const checklists = useQuery<ChecklistDay, ApiError>({
    queryKey: ['today', 'checklists'],
    queryFn: () => shiftApi.checklists(),
    enabled: seesShift,
    ...FRESH,
  });

  const tiles: Tile[] = [];

  // Пустая система отвечает обзором с `started: false` — плиток нет, и
  // нули вместо них человек прочёл бы как провал выручки.
  if (overview.data?.started) {
    for (const metric of overview.data.tiles.slice(0, 3)) {
      tiles.push({
        key: metric.code,
        title: metric.name,
        value: metricValue(metric),
        hint: overview.data.period.text,
        link: '/finance',
      });
    }
  }
  if (week.data) {
    tiles.push({
      key: 'week',
      title: 'К оплате на этой неделе',
      value: rub(week.data.total) ?? '—',
      hint: week.data.orders === 0 ? 'платёжек нет' : `платёжек: ${week.data.orders}`,
      link: '/payments',
    });
  }
  if (debts.data && debts.data.overdue.count > 0) {
    tiles.push({
      key: 'overdue',
      title: 'Просрочено',
      value: rub(debts.data.overdue.total) ?? '—',
      hint: `накладных: ${debts.data.overdue.count}`,
      link: '/payments/debts?list=overdue',
      tone: 'red',
    });
  }
  if (checklists.data) {
    const { planned, done, done_late: late, missed } = checklists.data.totals;
    tiles.push({
      key: 'checklists',
      title: 'Чек-листы сегодня',
      value: `${done + late} из ${planned}`,
      hint: missed > 0 ? `не сделано: ${missed}` : 'пропущенных нет',
      link: '/shift',
      tone: missed > 0 ? 'red' : null,
    });
  }

  if (tiles.length === 0) return null;

  return (
    <section className="numbers" aria-label="Главные цифры">
      {tiles.map((tile) => (
        <Link
          key={tile.key}
          to={tile.link}
          className={`numbers__tile${tile.tone === 'red' ? ' numbers__tile--red' : ''}`}
        >
          <span className="numbers__title">{tile.title}</span>
          <span className="numbers__value">{tile.value}</span>
          {tile.hint && <span className="numbers__hint">{tile.hint}</span>}
        </Link>
      ))}
    </section>
  );
}
